import { isFilled } from '@prismicio/client';
import { SliceZone } from '@prismicio/react';
import { createClient } from 'prismicio';

import cx from 'classnames';

import type { Content } from '@prismicio/client';
import { PrismicNextImage } from 'components/PRISMIC/PrismicNextImage';
import { components } from 'components/PRISMIC/slices';
import { Layout } from 'layout';
import { GetStaticPropsContext } from 'next';
import Head from 'next/head';
import { createContext, useContext, type JSX } from 'react';
import GamesLogo from './_GamesLogo';

type PageProps = { page: Content.GamesDocument };

type PageParams = { uid: string };

const GamesTedContext = createContext<{ mixpanelContext: string | null }>({
  mixpanelContext: null
});

export const useGamesTedContext = () => useContext(GamesTedContext);

const Games = ({ page }: PageProps): JSX.Element => {
  const hasPartnerLogo = isFilled.image(page.data.partnerLogo);

  return (
    <Layout addMinHeight={false}>
      <Head>
        <title>{page.data.meta_title}</title>
        <style>
          {
            /* CSS */ `
            @font-face {
              font-family: 'GT America';
              src: local('GT America'),
                   url('https://pl.tedcdn.com/fonts/GT-America-Regular.woff2') format('woff2');
              font-weight: normal;
              font-style: normal;
            }
            @font-face {
              font-family: 'GT America';
              src: local('GT America Bold'),
                   url('https://pl.tedcdn.com/fonts/GT-America-Bold.woff2') format('woff2');
              font-weight: 700;
              font-style: normal;
            }
            @font-face {
              font-family: 'GT America Condensed';
              src: local('GT America Condensed'),
                   url('https://pl.tedcdn.com/fonts/GT-America-Condensed.woff2') format('woff2');
              font-weight: normal;
              font-style: normal;
            }
            @font-face {
              font-family: 'GT America Condensed';
              src: local('GT America Condensed Bold'),
                   url('https://pl.tedcdn.com/fonts/GT-America-Condensed-Bold.woff2') format('woff2');
              font-weight: 700;
              font-style: normal;
            }
            .font-gt-america {
              font-family: 'GT America', sans-serif;
            }
            .font-gt-america-condensed {
              font-family: 'GT America Condensed', sans-serif;
            }
          `
          }
        </style>
      </Head>
      <div className="font-gt-america contents">
        <div
          className={cx(
            'relative overflow-hidden bg-offWhite py-8 md:py-10',
            hasPartnerLogo && 'pb-6'
          )}
        >
          <div className="flex w-full flex-col items-center md:container md:justify-start">
            <div className="flex w-full flex-col items-center gap-2 px-5 md:w-fit md:items-start md:px-0">
              <GamesLogo />
              {hasPartnerLogo && (
                <PrismicNextImage
                  className="max-w-36 self-start sm-tui:max-w-none"
                  width={275}
                  field={page.data.partnerLogo}
                  alt={page.data.partnerLogo.alt || ''}
                />
              )}
            </div>
          </div>

          {page.data.showBeta && (
            <>
              <svg
                viewBox="0 0 94 95"
                width={94}
                height={95}
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="absolute bottom-0 end-0 block lg:hidden"
              >
                <path
                  d="M94.4844 0L94.4844 47.566L47.4844 94.5L-0.000286418 94.5L94.4844 0Z"
                  fill="#6041FF"
                />
                <path
                  d="M46.7029 71.1343C48.052 70.1415 49.5285 70.2434 50.7249 71.4398C52.1504 72.8653 52.265 74.9909 50.2031 77.0528L45.9647 81.2912L36.9278 72.2544L41.0008 68.1814C43.0627 66.1195 45.0101 66.1832 46.3338 67.5069C47.2756 68.4487 47.4793 69.7979 46.7029 71.1343ZM41.9426 70.2688L40.5935 71.618L42.7954 73.8199L44.1446 72.4708C45.1119 71.5034 44.9846 70.6379 44.3737 70.027C43.7373 69.3906 42.8718 69.3397 41.9426 70.2688ZM48.0902 76.1364C49.0703 75.1564 48.9939 74.189 48.3066 73.5017C47.6066 72.8017 46.6647 72.8017 45.761 73.7054L44.2209 75.2454L46.6011 77.6256L48.0902 76.1364ZM55.57 68.5039L59.6938 64.3801L61.2848 65.9711L55.0099 72.2459L45.9731 63.2091L52.1589 57.0233L53.7499 58.6143L49.7151 62.6491L51.7516 64.6855L55.57 60.8672L57.1482 62.4454L53.3299 66.2638L55.57 68.5039ZM60.7899 48.3923L62.4954 50.0979L59.8608 52.7325L67.1921 60.0638L65.0538 62.2021L57.7225 54.8708L55.1005 57.4928L53.395 55.7872L60.7899 48.3923ZM76.1643 51.0915L73.3642 49.666L70.0295 53.0007L71.455 55.8009L69.304 57.9519L63.551 45.6313L66.0075 43.1748L78.3281 48.9278L76.1643 51.0915ZM69.0112 50.9897L71.3532 48.6478L68.0057 46.955C67.4839 46.6877 67.0257 46.4586 66.402 46.0131L66.3766 46.0385C66.822 46.6622 67.0511 47.1204 67.3184 47.6423L69.0112 50.9897Z"
                  fill="#fff"
                />
              </svg>
              <svg
                className="absolute bottom-0 end-0 hidden lg:block"
                viewBox="0 0 207 207"
                width={207}
                height={207}
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <title>BETA</title>
                <path
                  d="M207 0L207 86.064L84.0654 207L2.14693e-05 207L207 0Z"
                  fill="#6041FF"
                />
                <path
                  d="M107.712 140.145C109.792 138.615 112.069 138.772 113.914 140.616C116.112 142.814 116.288 146.092 113.109 149.271L106.574 155.806L92.6399 141.872L98.9199 135.592C102.099 132.413 105.102 132.511 107.143 134.552C108.595 136.004 108.909 138.085 107.712 140.145ZM100.372 138.811L98.2919 140.891L101.687 144.286L103.767 142.206C105.259 140.714 105.063 139.38 104.121 138.438C103.139 137.457 101.805 137.378 100.372 138.811ZM109.851 147.858C111.362 146.347 111.245 144.855 110.185 143.796C109.105 142.716 107.653 142.716 106.26 144.11L103.885 146.484L107.555 150.154L109.851 147.858ZM121.384 136.09L127.743 129.731L130.196 132.184L120.521 141.859L106.587 127.925L116.125 118.388L118.578 120.841L112.357 127.062L115.497 130.202L121.384 124.314L123.818 126.748L117.93 132.636L121.384 136.09ZM129.433 105.079L132.063 107.709L128 111.772L139.304 123.076L136.007 126.373L124.703 115.069L120.66 119.111L118.031 116.482L129.433 105.079ZM153.139 109.241L148.821 107.043L143.679 112.185L145.877 116.503L142.561 119.819L133.69 100.822L137.478 97.0345L156.475 105.905L153.139 109.241ZM142.109 109.084L145.72 105.473L140.559 102.863C139.754 102.451 139.048 102.098 138.086 101.411L138.047 101.45C138.734 102.412 139.087 103.118 139.499 103.923L142.109 109.084Z"
                  fill="#fff"
                />
              </svg>
            </>
          )}
        </div>
        <GamesTedContext
          value={{ mixpanelContext: `${page.data.mixpanelContext}` }}
        >
          <SliceZone slices={page.data.slices} components={components} />
        </GamesTedContext>
      </div>
    </Layout>
  );
};

export default Games;

export async function getStaticProps({
  previewData
}: GetStaticPropsContext<PageParams>) {
  const client = createClient({ previewData });
  // Should only have one type of page in prismic for this
  const page = await client.getSingle('games');

  return {
    props: {
      page
    },
    revalidate: 60
  };
}
